import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { FASHION_CATEGORIES } from "@/lib/categories";
import { MobileMenu } from "@/components/MobileMenu";
import { SearchOverlay } from "@/components/SearchOverlay";

export function FashionHeader() {
  const { user, setAuthOpen } = useAuth();
  const { totalItems } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(null); // "women" | "men" | null
  const closeTimer = useRef(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => { setMobileOpen(false); }, [navigate]);

  const openMega = (slug) => {
    clearTimeout(closeTimer.current);
    setMegaOpen(slug);
  };
  const closeMega = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(null), 200);
  };
  const keepMega = () => clearTimeout(closeTimer.current);

  const activeCat = FASHION_CATEGORIES.find((c) => c.slug === megaOpen);

  return (
    <>
      {/* ── Announcement Bar ── */}
      <div className="fashion-announcement hidden md:block">
        Complimentary shipping on orders above ₹2,999
      </div>

      {/* ── Main Header ── */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_var(--fashion-border)]"
            : "bg-[var(--fashion-bg)]"
        }`}
      >
        <div className="max-w-fashion mx-auto px-4 md:px-8">
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between h-[72px]">
            {/* Left Nav */}
            <nav className="flex items-center gap-8">
              {FASHION_CATEGORIES.map((cat) => (
                <div
                  key={cat.slug}
                  onMouseEnter={() => openMega(cat.slug)}
                  onMouseLeave={closeMega}
                >
                  <NavLink
                    to={`/${cat.slug}`}
                    className={({ isActive }) =>
                      `text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 pb-1 ${
                        isActive || megaOpen === cat.slug
                          ? "text-fashion-text border-b border-fashion-text"
                          : "text-fashion-text-muted hover:text-fashion-text"
                      }`
                    }
                  >
                    {cat.name}
                  </NavLink>
                </div>
              ))}
              <NavLink
                to="/new-arrivals"
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 pb-1 ${
                    isActive ? "text-fashion-text border-b border-fashion-text" : "text-fashion-text-muted hover:text-fashion-text"
                  }`
                }
              >
                New Arrivals
              </NavLink>
              <NavLink
                to="/collections"
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 pb-1 ${
                    isActive ? "text-fashion-text border-b border-fashion-text" : "text-fashion-text-muted hover:text-fashion-text"
                  }`
                }
              >
                Collections
              </NavLink>
            </nav>

            {/* Center Logo */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
              <span className="font-display text-[22px] font-light tracking-[0.15em] uppercase text-fashion-text">
                Farniymart
              </span>
            </Link>

            {/* Right Actions */}
            <div className="flex items-center gap-5">
              <button
                onClick={() => setSearchOpen(true)}
                className="text-fashion-text-muted hover:text-fashion-text transition-colors"
                aria-label="Search"
              >
                <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => user ? navigate("/account") : setAuthOpen(true)}
                className="text-fashion-text-muted hover:text-fashion-text transition-colors"
                aria-label="Account"
              >
                <User className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </button>
              <Link
                to="/wishlist"
                className="relative text-fashion-text-muted hover:text-fashion-text transition-colors"
                aria-label="Wishlist"
              >
                <Heart className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {wishCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-fashion-accent text-white text-[9px] font-semibold flex items-center justify-center">
                    {wishCount}
                  </span>
                )}
              </Link>
              <Link
                to="/cart"
                className="relative text-fashion-text-muted hover:text-fashion-text transition-colors"
                aria-label="Bag"
              >
                <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-fashion-accent text-white text-[9px] font-semibold flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="flex lg:hidden items-center justify-between h-14">
            <button
              onClick={() => setMobileOpen(true)}
              className="text-fashion-text p-1"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>

            <Link to="/" className="font-display text-lg font-light tracking-[0.12em] uppercase text-fashion-text">
              Farniymart
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                className="text-fashion-text p-1"
                aria-label="Search"
              >
                <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </button>
              <Link to="/wishlist" className="relative text-fashion-text p-1">
                <Heart className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {wishCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-fashion-accent text-white text-[8px] font-bold flex items-center justify-center">
                    {wishCount}
                  </span>
                )}
              </Link>
              <Link to="/cart" className="relative text-fashion-text p-1">
                <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-fashion-accent text-white text-[8px] font-bold flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* ── Desktop Mega Menu ── */}
        {megaOpen && activeCat && (
          <div
            className="hidden lg:block absolute left-0 right-0 bg-white border-t border-fashion-border shadow-lg z-50"
            onMouseEnter={keepMega}
            onMouseLeave={closeMega}
          >
            <div className="max-w-fashion mx-auto px-8 py-10 grid grid-cols-[1fr_280px] gap-12">
              <div>
                <p className="text-overline mb-6">{activeCat.name}'s Collection</p>
                <div className="grid grid-cols-4 gap-x-8 gap-y-3">
                  {activeCat.subcategories.map((sub) => (
                    <Link
                      key={sub.slug}
                      to={`/${activeCat.slug}/${sub.slug}`}
                      className="text-sm text-fashion-text-muted hover:text-fashion-text transition-colors duration-200"
                      onClick={() => setMegaOpen(null)}
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-8">
                  <Link
                    to={`/${activeCat.slug}`}
                    className="btn-fashion-ghost text-xs"
                    onClick={() => setMegaOpen(null)}
                  >
                    View All {activeCat.name} <ChevronRight className="h-3 w-3 ml-1" />
                  </Link>
                </div>
              </div>
              <div className="fashion-image-hover rounded-sm overflow-hidden">
                <img
                  src={activeCat.image}
                  alt={activeCat.name}
                  className="w-full h-[300px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── Mobile Menu ── */}
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />

      {/* ── Search Overlay ── */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
